import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock, User, Heart, Building2, KeyRound } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_agent-connect-93/artifacts/akzw3wcz_goldielocks-logo-2048.png";

const ROLES = [
  { id: 'buyer',  icon: Heart,     label: 'Buyer',  desc: 'Find your perfect home',         color: '#10B981', bg: '#10B98112' },
  { id: 'seller', icon: KeyRound,  label: 'Seller', desc: 'List your home, find buyers',    color: '#F97316', bg: '#F9731612' },
  { id: 'agent',  icon: Building2, label: 'Agent',  desc: 'Manage listings & grow leads',  color: '#0A192F', bg: '#0A192F12' },
];

export default function Register() {
  const { register, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [role, setRole] = useState(searchParams.get('role') || 'buyer');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const r = searchParams.get('role');
    if (r && ['buyer', 'agent', 'seller'].includes(r)) setRole(r);
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      const user = await register(name, email, password, role);
      if (user.role === 'buyer') navigate('/discover');
      else if (user.role === 'seller') navigate('/seller');
      else navigate('/agent');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Registration failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Goldielocks"
              className="h-16 w-16 rounded-2xl object-cover mx-auto mb-4"
              style={{ objectPosition: '50% 35%' }} />
            <h1 className="text-2xl font-black" style={{ color: '#0A192F' }}>Create your account</h1>
            <p className="text-slate-500 text-sm mt-1">Join Goldielocks and find your perfect match</p>
          </div>

          {/* Role selector */}
          <div className="grid grid-cols-3 gap-2 mb-6">
            {ROLES.map(r => (
              <button
                key={r.id}
                type="button"
                onClick={() => setRole(r.id)}
                data-testid={`register-role-${r.id}`}
                className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all duration-200"
                style={{
                  borderColor: role === r.id ? r.color : '#E2E8F0',
                  background: role === r.id ? r.bg : 'transparent',
                }}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: role === r.id ? r.color : '#E2E8F0' }}>
                  <r.icon size={15} className={role === r.id ? 'text-white' : 'text-slate-500'} />
                </div>
                <span className="text-xs font-bold" style={{ color: role === r.id ? r.color : '#64748B' }}>{r.label}</span>
                <span className="text-xs text-slate-400 text-center leading-tight hidden sm:block">{r.desc}</span>
              </button>
            ))}
          </div>

          {error && (
            <div className="mb-5 p-3 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm font-medium" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Full Name</label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" required
                  data-testid="register-name"
                  className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:border-transparent transition-all" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required
                  data-testid="register-email"
                  className="w-full pl-9 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:border-transparent transition-all" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 6 characters" required
                  data-testid="register-password"
                  className="w-full pl-9 pr-10 py-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:border-transparent transition-all" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full py-3.5 rounded-xl text-sm font-bold text-white transition-all duration-200 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: role === 'seller' ? '#F97316' : '#0A192F' }}>
              {loading ? 'Creating account...' : `Create ${role === 'buyer' ? 'Buyer' : role === 'seller' ? 'Seller' : 'Agent'} Account`}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            Already have an account?{' '}
            <Link to="/login" data-testid="register-login-link" className="font-semibold hover:underline" style={{ color: '#10B981' }}>
              Sign in
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
